import React, { useCallback, useEffect, useState } from 'react';
import { Wallet, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GREEN = '#2E6A4C';
const PRESETS = [20, 50, 100, 200];
const eur = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;

const verifyTopupReturn = async (token) => {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('topup');
  if (status === 'cancelled') toast.info('Rechargement annulé');
  if (status !== 'success' || !params.get('session')) return status;
  try {
    const r = await fetch(`${API_URL}/api/pos/portal/topup-stripe/verify/${params.get('session')}`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    if (d.status === 'confirmed') toast.success(`Compte rechargé${d.receipt_number ? ` — ticket ${d.receipt_number}` : ''}`);
    else toast.info('Paiement en cours de traitement…');
  } catch (e) { /* silencieux */ }
  return status;
};

/* Solde du compte client + rechargement en ligne (montants proposés ou montant libre). */
export const WalletTopupCard = ({ token, returnTab, onBalance, hideBalance = false, testId = 'wallet-topup' }) => {
  const [balance, setBalance] = useState(null);
  const [amount, setAmount] = useState('50');
  const [custom, setCustom] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/pos/portal/my-account`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const b = Number((await r.json()).balance) || 0; setBalance(b); onBalance?.(b); }
    } catch (e) { /* silencieux */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    (async () => {
      const status = await verifyTopupReturn(token);
      if (status) window.history.replaceState({}, '', `${window.location.pathname}?tab=${returnTab}`);
      load();
    })();
  }, [load, returnTab, token]);

  const amt = Math.round((parseFloat(amount) || 0) * 100) / 100;
  const valid = amt >= 5 && amt <= 2000;

  const start = async () => {
    if (!valid) { toast.error('Montant entre 5 € et 2000 €'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/pos/portal/topup-stripe/create-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt, origin_url: `${window.location.origin}${window.location.pathname}?tab=${returnTab}` }),
      });
      const d = await r.json();
      if (!r.ok || !d.url) throw new Error(typeof d?.detail === 'string' ? d.detail : 'Paiement indisponible');
      window.location.href = d.url;
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  const chip = (active) => `h-9 rounded-full text-xs font-bold transition-colors ${active ? 'text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-emerald-50'}`;

  return (
    <div className="rounded-2xl p-4 space-y-3" style={{ backgroundColor: 'rgba(255,255,255,0.92)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }} data-testid={testId}>
      {!hideBalance && (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E8F5E9' }}>
            <Wallet size={18} style={{ color: GREEN }} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase font-semibold tracking-wider text-neutral-500">Solde de mon compte</p>
            <p className="text-2xl font-black leading-tight" style={{ color: balance < 0 ? '#b91c1c' : GREEN }} data-testid={`${testId}-balance`}>
              {balance == null ? '…' : eur(balance)}
            </p>
          </div>
        </div>
      )}
      <div>
        <p className="text-xs font-semibold text-neutral-700 mb-2">Recharger mon compte en ligne</p>
        <div className="grid grid-cols-5 gap-1.5">
          {PRESETS.map(v => (
            <button key={v} type="button" onClick={() => { setCustom(false); setAmount(String(v)); }}
              className={chip(!custom && amount === String(v))} style={!custom && amount === String(v) ? { backgroundColor: GREEN } : undefined}
              data-testid={`${testId}-preset-${v}`}>{v} €</button>
          ))}
          <button type="button" onClick={() => { setCustom(true); setAmount(''); }} className={chip(custom)} style={custom ? { backgroundColor: GREEN } : undefined}
            data-testid={`${testId}-custom-btn`}>Autre</button>
        </div>
        {custom && (
          <input type="number" min="5" max="2000" step="1" inputMode="decimal" autoFocus value={amount} onChange={e => setAmount(e.target.value)}
            placeholder="Montant libre (5 à 2000 €)" className="mt-2 w-full h-10 border rounded-lg px-3 text-sm font-semibold"
            data-testid={`${testId}-custom-input`} />
        )}
      </div>
      <button type="button" onClick={start} disabled={busy || !valid}
        className="w-full h-10 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-opacity"
        style={{ backgroundColor: GREEN }} data-testid={`${testId}-pay-btn`}>
        {busy ? <><Loader2 size={14} className="animate-spin" /> Redirection…</> : <><CreditCard size={14} /> {valid ? `Recharger ${eur(amt)}` : 'Choisissez un montant'}</>}
      </button>
      <p className="text-[10px] text-neutral-500">Paiement sécurisé par carte. Le montant est disponible tout de suite pour vos départs, la boutique et la caisse.</p>
    </div>
  );
};
